import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, Package } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetPassword } from '@/lib/auth-client';
import { BUILD_STAMP } from '@/lib/build-info';

/** Kept in step with the server; the form should not accept what it will refuse. */
const MIN_PASSWORD_LENGTH = 6;

/** Where the emailed link lands. The token comes from the query string. */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  // better-auth appends ?error=INVALID_TOKEN rather than a token when the link
  // has already been used or has expired.
  const linkError = params.get('error');

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    // Checked here rather than only on the server: getting it wrong means being
    // locked out again, by a password nobody knows.
    if (password !== confirmation) {
      setError('Those two do not match.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: resetError } = await resetPassword({ newPassword: password, token });

      if (resetError) {
        setError(
          resetError.status === 429
            ? 'Too many attempts. Wait a minute and try again.'
            : 'That link has expired or has already been used. Ask for a new one.',
        );
        return;
      }

      setDone(true);
    } catch {
      setError('Could not reach the server. Check that the app is running.');
    } finally {
      setSubmitting(false);
    }
  };

  const unusable = !token || linkError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ShipSwift</h1>
          <p className="text-sm text-slate-500">Gold Star Printing</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 space-y-4">
          {done ? (
            <>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">Password changed</p>
                  <p className="text-sm text-slate-600 mt-1">
                    You can sign in with it now. Anywhere else you were signed in has been
                    signed out.
                  </p>
                </div>
              </div>
              <Link to="/login" className="block">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">Sign in</Button>
              </Link>
            </>
          ) : unusable ? (
            <>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">This link cannot be used</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Reset links work once and expire after an hour. Ask for a new one.
                  </p>
                </div>
              </div>
              <Link to="/forgot-password" className="block">
                <Button variant="outline" className="w-full">
                  Send a new link
                </Button>
              </Link>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <h2 className="font-semibold text-slate-900">Choose a new password</h2>
                <p className="text-sm text-slate-600 mt-1">
                  At least {MIN_PASSWORD_LENGTH} characters.
                </p>
              </div>

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmation">Type it again</Label>
                <Input
                  id="confirmation"
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save new password'
                )}
              </Button>
            </form>
          )}
        </div>

        <p className="text-xs text-center text-slate-400 mt-6">{BUILD_STAMP}</p>
      </div>
    </div>
  );
}
